import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { DateRange } from 'react-day-picker';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@shared/components/ui/shadcn/dialog';
import { Button } from '@shared/components/ui/button';
import { DateRangePicker } from '@shared/components/ui/DateRangePicker';
import { DynamicCombobox } from '@shared/components/ui/DynamicCombobox';
import { useInvoiceAuditInvoiceOptions } from '../../hooks/useInvoiceAuditInvoiceOptions';
import type { ThreePlReportDefinition } from '../../models/reports';

export interface DateRangeValue {
  dateFrom: string;
  dateTo: string;
}

export interface InvoiceSelectionValue {
  invoiceId: number;
  invoiceLabel: string;
}

interface GenerateReportDialogProps {
  report: ThreePlReportDefinition | null;
  open: boolean;
  isExporting: boolean;
  getDefaultRange: () => DateRangeValue;
  onOpenChange: (open: boolean) => void;
  onSubmitDateRange: (range: DateRangeValue) => void;
  onSubmitInvoice: (invoice: InvoiceSelectionValue) => void;
}

const formatDateLocal = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

export function GenerateReportDialog({
  report,
  open,
  isExporting,
  getDefaultRange,
  onOpenChange,
  onSubmitDateRange,
  onSubmitInvoice,
}: GenerateReportDialogProps) {
  const { t } = useTranslation('threePlExcelReportsModule');
  const { searchInvoices, resolveInvoiceOption } = useInvoiceAuditInvoiceOptions();
  const [range, setRange] = useState<DateRangeValue>(() => getDefaultRange());
  const [invoiceId, setInvoiceId] = useState('');
  const [invoiceLabel, setInvoiceLabel] = useState('');
  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (open && !wasOpenRef.current) {
      setRange(getDefaultRange());
      setInvoiceId('');
      setInvoiceLabel('');
    }
    wasOpenRef.current = open;
  }, [open, getDefaultRange]);

  const handleDateRangeUpdate = useCallback(({ range: r }: { range: DateRange }) => {
    if (!r.from || !r.to) return;
    setRange({
      dateFrom: formatDateLocal(r.from),
      dateTo: formatDateLocal(r.to),
    });
  }, []);

  const handleInvoiceChange = useCallback(
    async (value: string) => {
      setInvoiceId(value);
      if (!value.trim()) {
        setInvoiceLabel('');
        return;
      }

      const option = await resolveInvoiceOption(value);
      setInvoiceLabel(option?.label ?? '');
    },
    [resolveInvoiceOption],
  );

  const isDateRangeValid = range.dateFrom.length > 0 && range.dateTo.length > 0 && range.dateFrom <= range.dateTo;
  const isInvoiceValid = invoiceId.trim().length > 0;

  const handleClose = useCallback(
    (nextOpen: boolean) => {
      onOpenChange(nextOpen);
    },
    [onOpenChange],
  );

  const handleSubmit = () => {
    if (isExporting || !report) return;

    if (report.paramType === 'date_range') {
      if (!isDateRangeValid) return;
      onSubmitDateRange(range);
      return;
    }

    if (!isInvoiceValid) return;
    onSubmitInvoice({
      invoiceId: Number(invoiceId),
      invoiceLabel: invoiceLabel || `#${invoiceId}`,
    });
  };

  if (!report) return null;

  const isSubmitDisabled = isExporting || (report.paramType === 'date_range' ? !isDateRangeValid : !isInvoiceValid);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md transition-colors" data-testid="three-pl-export-dialog">
        <DialogHeader>
          <DialogTitle className="text-foreground">{t('dialog.title')}</DialogTitle>
          <DialogDescription className="text-muted-foreground">{t(report.titleKey)}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {report.paramType === 'date_range' ? (
            <>
              <div className="space-y-1.5">
                <span className="text-sm font-medium text-foreground">{t('dialog.period')}</span>
                <DateRangePicker
                  initialDateFrom={range.dateFrom}
                  initialDateTo={range.dateTo}
                  onUpdate={handleDateRangeUpdate}
                  align="start"
                  showPresets
                  triggerClassName="bg-card-bg border-card-border w-full transition-colors"
                  placeholder={t('dialog.period_placeholder')}
                  dataTestId="three-pl-export-date-range"
                />
              </div>
              <p className="text-xs text-muted-foreground">{t('dialog.hint')}</p>
            </>
          ) : (
            <>
              <div className="space-y-1.5">
                <span className="text-sm font-medium text-foreground">{t('dialog.invoice')}</span>
                <DynamicCombobox
                  value={invoiceId}
                  setValue={handleInvoiceChange}
                  placeholder={t('dialog.invoice_placeholder')}
                  searchFn={searchInvoices}
                  resolveSelectedOption={resolveInvoiceOption}
                  dataTestId="three-pl-export-invoice"
                  emptyMessage={t('dialog.invoice_empty')}
                />
              </div>
              <p className="text-xs text-muted-foreground">{t('dialog.invoice_hint')}</p>
            </>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="outline" onClick={() => handleClose(false)} disabled={false}>
            {t('dialog.cancel')}
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={isSubmitDisabled} data-testid="three-pl-export-submit">
            {isExporting ? t('dialog.submitting') : t('dialog.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
